import { Field } from '@base-ui/react/field';
import { Input } from '@base-ui/react/input';
import type { ComponentProps, ReactNode } from 'react';
import { cx } from '../lib/styled.tsx';
import {
  controlShell,
  controlShellInput,
  fieldError,
  fieldHint,
  fieldLabel,
  fieldOptional,
} from '../lib/surfaces.ts';

export interface TextFieldProps extends Omit<ComponentProps<typeof Input>, 'children'> {
  label: ReactNode;
  /** Marks the field as not required, in the label — "optional", "по желанию". */
  optional?: ReactNode;
  description?: ReactNode;
  /** Rendered when the native/Base UI validation for this control fails. */
  error?: ReactNode;
  /**
   * A control rendered inside the field, against its trailing edge — the
   * "read my site" button pattern. Usually a small `Button`.
   */
  action?: ReactNode;
}

/**
 * Label + control + description + error, wired through Base UI's Field so the
 * accessible relationships (aria-describedby, aria-invalid) come for free.
 */
export function TextField({
  label,
  optional,
  description,
  error,
  action,
  className,
  name,
  ...inputProps
}: TextFieldProps) {
  return (
    // `group` so the shell below can read the field's invalid and disabled state.
    <Field.Root name={name} className="group flex flex-col gap-1.5">
      <Field.Label className={fieldLabel}>
        {label}
        {optional ? <span className={fieldOptional}>{optional}</span> : null}
      </Field.Label>
      <div className={controlShell}>
        <Input className={cx(controlShellInput, className)} {...inputProps} />
        {action}
      </div>
      {description ? (
        <Field.Description className={fieldHint}>{description}</Field.Description>
      ) : null}
      <Field.Error className={fieldError}>{error}</Field.Error>
    </Field.Root>
  );
}
